/* ------------------------------------------- a comemoração de promoção ---
   O momento em que a pessoa cruza um degrau da régua de `TITULOS` e deixa de
   ser Curioso para virar Iniciante. Não é um diálogo nem uma faixa que se
   fecha: é o próprio cabeçalho da tela de resultado trocando de manchete
   enquanto a lição continua prestando contas nos chips logo abaixo. Zero
   clique a mais, zero foco roubado — a tela já tem um `Dialog` (o card de
   conquista) e dois modais no mesmo instante é bug de foco garantido.

   INVARIANTE do domínio, e é ela que autoriza o código a ser tão curto:
   `subiuDeTitulo` é `titulo !== tituloAnterior`, e `xpGanho` nunca é
   negativo — logo mudança de título só pode ser promoção. Não existe
   rebaixamento a tratar, e escrever a guarda mesmo assim inventaria um caso
   que o domínio não produz (e que ninguém saberia testar de verdade).

   SEM ESTADO: nenhum `useState`, `useEffect`, ref ou timer. É função pura de
   props. Também não se fecha sozinho — não há botão de fechar nem contagem
   regressiva: a comemoração é a tela, e sai quando a pessoa clica em
   "Voltar à trilha".

   Atenção ao editar o CSS daqui: comentário dentro de um bloco `styled` mora
   numa template string, então crase e cifrão-chave viram sintaxe. A prosa
   dos comentários de CSS deste arquivo usa aspas de propósito. */
import {Chip, Typography} from '@mui/material'
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded'
import styled from 'styled-components'
import {getCurrentColors} from '../../../utils/colors'
import {proximoTitulo} from '../dominio'
import {
    DURACAO,
    SUAVE,
    chover,
    estourar,
    semMovimento,
    surgir,
} from './movimento'

const COLORS = getCurrentColors()

/* ------------------------------------------------------------ o confete ---
   Constante de MÓDULO, calculada uma única vez na importação. Nenhum
   `Math.random` em lugar nenhum do arquivo: sorteio em render faria cada
   peça reiniciar a queda a cada re-render do React (o pai re-renderiza a
   cada clique em qualquer lugar da tela de resultado) e nada seria estável
   em teste.

   O espalhamento vem da razão áurea porque `((i+1)·φ) mod 1` distribui os
   pontos pelo intervalo sem repetir e sem enfileirar — é o mesmo truque das
   sementes de girassol. Uma divisão uniforme (`i/24`) daria colunas em fila
   e leria como cortina, não como festa. */
const PHI = 1.618033988749895

/* Cores FIXAS do produto, deliberadamente fora da paleta semântica e fora do
   tenant. Verde-acerto (#2E7D32) e vermelho-erro têm significado nesta tela e
   não podem cair do céu como enfeite; e confete que muda de cor com o
   subdomínio não é festa, é tema. O #3FFE81 é o `--brand-mint` do licito.guru
   (src/styles/tokens.css): a festa é da marca do PRODUTO, e essa não muda de
   subdomínio para subdomínio. */
const CORES_DO_CONFETE = ['#F2B705', '#FF8A5B', '#3FFE81', '#8C6BF0', '#4EA8FF']

const PECAS = Array.from({length: 24}, (_, i) => ({
    esquerda: 2 + (((i + 1) * PHI) % 1) * 96,
    cor: CORES_DO_CONFETE[i % CORES_DO_CONFETE.length],
    // Uma redonda a cada quatro: só retângulos lê como pixel solto na tela.
    redonda: i % 4 === 0,
    // Durações e atrasos em passos primos entre si (5 e 8) para que a mesma
    // combinação não se repita dentro das 24 peças — duas peças idênticas
    // caindo lado a lado denunciam o padrão.
    duracao: 2.2 + (i % 5) * 0.2,
    atraso: (i % 8) * 90,
}))

const Camada = styled.div`
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
    z-index: 2;

    /* No celular a mesma chuva num viewport de 360px é poluição sobre o
       texto. Cortar por posição (e não sorteando um subconjunto) mantém a
       lista determinística: some sempre a mesma metade. */
    @media (max-width: 640px) {
        & > *:nth-child(n + 15) {
            display: none;
        }
    }
`

const Peca = styled.span<{
    $esquerda: number
    $cor: string
    $redonda: boolean
    $duracao: number
    $atraso: number
}>`
    position: absolute;
    top: -14px;
    left: ${({$esquerda}) => $esquerda}%;
    width: 8px;
    height: ${({$redonda}) => ($redonda ? '8px' : '13px')};
    border-radius: ${({$redonda}) => ($redonda ? '50%' : '2px')};
    background-color: ${({$cor}) => $cor};
    /* EXCEÇÃO DECLARADA à regra dos quadros-chave: "chover" TERMINA em
       opacidade zero por definição, e isto é decoração pura sem informação
       nenhuma. Nascer invisível é justamente o que faz a preferência por
       menos movimento APAGAR o confete em vez de congelar 24 quadradinhos
       parados por cima do texto. Nenhum elemento com conteúdo nasce
       invisível neste arquivo. */
    opacity: 0;
    animation: ${chover} ${({$duracao}) => $duracao}s linear
        ${({$atraso}) => $atraso}ms both;
    ${semMovimento}
`

/* Não desmonta quando a queda acaba: as peças terminam em opacidade zero e a
   camada é `pointer-events: none`, então elas não estorvam nada. Um
   `onAnimationEnd` + `setState` custaria um re-render por peça para não
   mudar um pixel — e em jsdom o evento nem dispara, o que deixaria o teste
   mentindo sobre o que acontece no navegador. */
function Confete() {
    return (
        <Camada aria-hidden="true">
            {PECAS.map((peca, i) => (
                <Peca
                    key={i}
                    $esquerda={peca.esquerda}
                    $cor={peca.cor}
                    $redonda={peca.redonda}
                    $duracao={peca.duracao}
                    $atraso={peca.atraso}
                />
            ))}
        </Camada>
    )
}

/* ---------------------------------------------------------- o cenário ----
   Bloco NÃO é posicionado, de propósito: a camada de confete se ancora no
   "position: relative" da tela de resultado inteira e chove sobre ela, não
   só sobre estas duas linhas.

   As duas medidas de espaço são deste componente e não do pai porque o pai
   empilha esta comemoração logo abaixo de uma legenda pequena ("LIÇÃO
   CONCLUÍDA"): sem a margem de topo a fita de títulos encostaria nela e as
   duas linhas leriam como um bloco só. E o intervalo é 12px, não 8px, porque
   agora o herói tem duas linhas apenas — a manchete precisa respirar da fita
   para continuar sendo a linha grande da tela. */
const Bloco = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    animation: ${surgir} ${DURACAO.lenta} ${SUAVE} both;
    ${semMovimento}
`

const Fita = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    /* A seta herda daqui: pintar o próprio ícone disputaria especificidade
       com as regras do MuiSvgIcon. */
    color: ${COLORS.cinzaEscuro};
`

/* A CADÊNCIA da fita abre em 60ms: o "surgir" do Bloco dura 320ms e precisa
   de alguns quadros de dianteira, senão a fita sobe junto com o próprio
   contêiner e os dois movimentos viram um borrão só. Daí em diante são 140ms
   (a seta), 220ms (o título novo) e 320ms (manchete e próximo alvo), na ordem
   em que a frase se lê: de onde, para onde, e o que isso significa. Não
   atrase mais do que isso — o herói tem duas linhas apenas, e enquanto os
   chips não entram ele está literalmente vazio.

   O esmaecido do título antigo mora no Chip e a entrada mora no span em
   volta. "surgir" termina em opacidade 1 e, com preenchimento "both", esse
   valor final gruda no elemento animado: aplicado no próprio chip, ele
   apagaria o esmaecido no instante em que a entrada acabasse. Dois
   elementos, duas opacidades, e o que se vê é o produto das duas. */
const EntradaDoChipAnterior = styled.span`
    display: inline-flex;
    animation: ${surgir} ${DURACAO.base} ${SUAVE} 60ms both;
    ${semMovimento}
`

const EntradaDoChipNovo = styled.span`
    display: inline-flex;
    animation: ${estourar} 520ms ${SUAVE} 220ms both;
    ${semMovimento}
`

const Seta = styled(ArrowRightAltRoundedIcon)`
    animation: ${surgir} ${DURACAO.base} ${SUAVE} 140ms both;
    ${semMovimento}
`

const EntradaDoTexto = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    animation: ${surgir} ${DURACAO.lenta} ${SUAVE} 320ms both;
    ${semMovimento}
`

/* Local, e não o "visuallyHidden" do @mui/utils: aquele pacote chega aqui
   como dependência TRANSITIVA do MUI e o package.json não o declara —
   importar dele é depender de uma árvore que um update pode reorganizar sem
   aviso. */
const SoParaLeitor = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
`

/* A régua de `TITULOS` continua tendo um dono só: o domínio. A tela pergunta
   qual é o próximo alvo a partir do XP JÁ somado da lição e escreve a frase.
   Número sem separador de milhar para casar com o `5200 XP` e o
   `0 / 150 XP para Iniciante` que já estão impressos nesta mesma tela. */
function linhaDoProximoAlvo(xpTotal: number): string {
    const proximo = proximoTitulo(xpTotal)
    if (!proximo) return 'Você chegou ao último título da jornada.'
    const faltam = proximo.xp - xpTotal
    if (faltam === 1) return `Falta 1 XP para ${proximo.nome}.`
    return `Faltam ${faltam} XP para ${proximo.nome}.`
}

interface SubiuDeNivelProps {
    tituloAnterior: string
    titulo: string
    /** XP total DEPOIS da lição: é dele que sai a linha do próximo alvo. */
    xpTotal: number
}

export default function SubiuDeNivel({
    tituloAnterior,
    titulo,
    xpTotal,
}: SubiuDeNivelProps) {
    return (
        <>
            {/* Fora do `Bloco`, e isso é obrigatório: `surgir` deixa um
                `transform` grudado no Bloco (preenchimento `both`), e
                elemento com transform vira bloco contêiner dos descendentes
                absolutos — a camada encolheria para o tamanho do bloco e a
                chuva ficaria presa nas duas linhas da comemoração, em vez de
                cair sobre a tela de resultado inteira. */}
            <Confete />
            {/* SEM `role`: a live region é o bloco herói da tela de
                resultado, que envolve este componente. Live region aninhada
                em live region faz o leitor de tela ler tudo duas vezes. */}
            <Bloco>
                <Fita>
                    <EntradaDoChipAnterior>
                        {/* Esmaecido, nunca tachado: título riscado lê como
                            cancelado, e a pessoa não perdeu Curioso — ela
                            passou por ele. */}
                        <Chip
                            label={tituloAnterior}
                            variant="outlined"
                            size="small"
                            sx={{opacity: 0.6}}
                        />
                    </EntradaDoChipAnterior>
                    {/* `SvgIcon` é aria-hidden por padrão, então sozinha a
                        seta seria um salto mudo de um nome para o outro. */}
                    <Seta />
                    <SoParaLeitor>para</SoParaLeitor>
                    <EntradaDoChipNovo>
                        <Chip
                            label={titulo}
                            sx={{
                                backgroundColor: COLORS.primariaEscura,
                                color: COLORS.branco,
                                fontWeight: 'bold',
                            }}
                        />
                    </EntradaDoChipNovo>
                </Fita>
                <EntradaDoTexto>
                    {/* A manchete diz o EVENTO; a fita diz os NOMES; a última
                        linha diz o próximo passo. "Você agora é Iniciante"
                        duplicaria a fita e desperdiçaria a única linha grande
                        da tela. */}
                    <Typography variant="h5" fontWeight="bold">
                        Você subiu de título!
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {linhaDoProximoAlvo(xpTotal)}
                    </Typography>
                </EntradaDoTexto>
            </Bloco>
        </>
    )
}
